import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  UserCheck,
  FileCheck,
  CreditCard,
  Globe,
  Lock,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Activity,
  Zap,
  Award as AwardIcon,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileSearch,
  Clipboard,
  BookOpen,
  Scale,
  Gavel,
  Badge,
  Certificate,
  Stamp,
  Seal,
  Receipt,
  Banknote,
  Wallet,
  Coins,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Calculator,
  Percent,
  Hash,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Cpu,
  HardDrive,
  Monitor,
  Wrench,
  Truck,
  Hammer,
  Drill,
  Saw,
  Tool,
  Cog,
  Gear,
  Layers,
  Grid,
  Layout,
  Maximize,
  Minimize,
  Move,
  RotateCcw,
  RotateCw,
  Square,
  Circle,
  Triangle,
  Hexagon,
  Octagon
} from 'lucide-react';
//import { useToast } from '../common/Toast';
//import { useToast } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ResourceAllocationService from '../../services/resourceAllocationService';
import SubcontractingService from '../../services/subcontractingService';
import API_CONFIG from '../../config/api';
import { useToast, ToastContainer } from '../common/Toast';

const CreateResourceAllocation = ({ editMode = false, viewMode = false, allocationId = null }) => {
  const navigate = useNavigate();
  const params = useParams();
  const { success, error: showError } = useToast();
  
  // Get allocationId from params if not provided as prop
  
  const finalViewMode = !!viewMode;
  const finalEditMode = !!editMode && !finalViewMode;

  const finalAllocationId = allocationId ?? params.id ?? null;
  const [humanResourceInput, setHumanResourceInput] = useState('');
  const [equipmentInput, setEquipmentInput] = useState('');
  const [requiredSkillInput, setRequiredSkillInput] = useState('');

  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    allocationId: '',
    allocationName: '',
    allocationType: 'project',
    status: 'draft',
    priority: 'medium',
    startDate: '',
    endDate: '',
    duration: 0,
    
    // Subcontractor Information
    subcontractor: {
      id: '',
      name: '',
      company: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: ''
    },
    
    // Project Information
    project: {
      id: '',
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      budget: '',
      status: ''
    },
    
    // Resource Details
    resources: {
      humanResources: [],
      equipment: [],
      materials: [],
      tools: [],
      vehicles: [],
      facilities: []
    },
    
    // Allocation Details
    allocation: {
      totalCapacity: 0,
      allocatedCapacity: 0,
      availableCapacity: 0,
      utilizationRate: 0,
      efficiency: 0,
      productivity: 0
    },
    
    // Scheduling Information
    schedule: {
      workDays: [],
      workHours: 8,
      shiftPattern: 'day',
      overtimeAllowed: false,
      weekendWork: false,
      holidayWork: false
    },
    
    // Skills and Requirements
    requirements: {
      requiredSkills: [],
      certifications: [],
      experience: '',
      education: '',
      language: '',
      physicalRequirements: []
    },
    
    // Cost Information
    costs: {
      hourlyRate: 0,
      dailyRate: 0,
      weeklyRate: 0,
      monthlyRate: 0,
      totalCost: 0,
      currency: 'USD',
      costCenter: '',
      budgetAllocation: 0
    },
    
    // Performance Metrics
    performance: {
      qualityScore: 0,
      timelinessScore: 0,
      safetyScore: 0,
      efficiencyScore: 0,
      overallScore: 0
    },
    
    // Location Information
    location: {
      primaryLocation: '',
      secondaryLocations: [],
      travelRequired: false,
      travelDistance: 0,
      accommodationRequired: false,
      accommodationType: ''
    },
    
    // Communication and Reporting
    communication: {
      reportingFrequency: 'weekly',
      reportingMethod: 'email',
      supervisor: '',
      teamLead: '',
      communicationChannels: []
    },
    
    // Quality and Safety
    quality: {
      qualityStandards: [],
      safetyRequirements: [],
      complianceStandards: [],
      inspectionSchedule: [],
      qualityMetrics: []
    },
    
    // Risk Management
    risk: {
      identifiedRisks: [],
      riskLevel: 'medium',
      mitigationStrategies: [],
      contingencyPlans: [],
      riskOwner: ''
    },
    
    // Documentation
    documentation: {
      contracts: [],
      agreements: [],
      permits: [],
      licenses: [],
      certificates: [],
      reports: []
    },
    
    // Approval and Review
    approval: {
      approver: '',
      approvalDate: '',
      status: 'pending',
      notes: '',
      nextReviewDate: ''
    },
    
    // Notifications and Alerts
    notifications: {
      emailAlerts: true,
      smsAlerts: false,
      portalNotifications: true,
      escalationEnabled: true,
      reminderFrequency: 'daily'
    },
    
    // Advanced Settings
    settings: {
      autoAllocation: false,
      requireApproval: true,
      publicVisibility: false,
      allowModifications: true,
      trackTime: true,
      trackCosts: true
    },
    
    // Recurring Allocation (if applicable)
    recurring: {
      enabled: false,
      frequency: 'weekly',
      startDate: '',
      endDate: '',
      nextAllocationDate: '',
      cycles: 0
    },
    
    // Dependencies
    dependencies: {
      prerequisiteAllocations: [],
      dependentAllocations: [],
      criticalPath: false,
      slackTime: 0
    },
    
    // Constraints
    constraints: {
      timeConstraints: [],
      resourceConstraints: [],
      budgetConstraints: [],
      skillConstraints: [],
      locationConstraints: []
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Load subcontractors for dropdown
  useEffect(() => {
    const loadSubcontractors = async () => {
      try {
        const response = await SubcontractingService.getSubcontractings({ limit: 100 });
        if (response && response.success) {
          setSubcontractors(response.data || []);
        }
      } catch (err) {
        console.error('Error loading subcontractors:', err);
      }
    };
    loadSubcontractors();
  }, []);
  
  // Load projects for dropdown
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}?limit=1000&isActive=true`, {
          headers: API_CONFIG.HEADERS
        });
        if (response.ok) {
          const data = await response.json();
          const projectsList = data.data?.projects || data.projects || [];
          setProjects(projectsList);
        }
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    };
    loadProjects();
  }, []);
  
  // Load allocation data if in edit or view mode
  useEffect(() => {
    const loadAllocationData = async () => {
      if (!finalAllocationId) return; 
        setLoading(true);
        try {
          const response = await ResourceAllocationService.getResourceAllocation(finalAllocationId);
          if (response && response.success) {
            const allocation = response.data;
            // Map backend data to form data
            setFormData({
              allocationId: allocation.allocationId || '',
              allocationName: allocation.allocationName || '',
              allocationType: allocation.allocationType || 'project',
              status: allocation.status || 'draft',
              priority: allocation.priority || 'medium',
              startDate: allocation.startDate ? new Date(allocation.startDate).toISOString().split('T')[0] : '',
              endDate: allocation.endDate ? new Date(allocation.endDate).toISOString().split('T')[0] : '',
              duration: allocation.duration || 0,
              subcontractor: allocation.subcontractor
              ? {
                id:
                allocation.subcontractor.id ||
                allocation.subcontractor._id ||
                allocation.subcontractor.subcontractorId ||
                '',
                name: allocation.subcontractor.name || '',
                company: allocation.subcontractor.company || allocation.subcontractor.name || '',
                contactPerson: allocation.subcontractor.contactPerson || '',
                email: allocation.subcontractor.email || '',
                phone: allocation.subcontractor.phone || '',
                address: allocation.subcontractor.address || ''
              }
              : {
                id: '',
                name: '',
                company: '',
                contactPerson: '',
                email: '',
                phone: '',
                address: ''
              },
              project: allocation.project || {
                id: '',
                name: '',
                description: '',
                startDate: '',
                endDate: '',
                budget: '',
                status: ''
              },
              resources: allocation.resources || {
                humanResources: [],
                equipment: [],
                materials: [],
                tools: [],
                vehicles: [],
                facilities: []
              },
              allocation: allocation.allocation || {
                totalCapacity: 0,
                allocatedCapacity: 0,
                availableCapacity: 0,
                utilizationRate: 0,
                efficiency: 0,
                productivity: 0
              },
              schedule: allocation.schedule || {
                workDays: [],
                workHours: 8,
                shiftPattern: 'day',
                overtimeAllowed: false,
                weekendWork: false,
                holidayWork: false
              },
              requirements: allocation.requirements || {
                requiredSkills: [],
                certifications: [],
                experience: '',
                education: '',
                language: '',
                physicalRequirements: []
              },
              costs: allocation.costs || {
                hourlyRate: 0,
                dailyRate: 0,
                weeklyRate: 0,
                monthlyRate: 0,
                totalCost: 0,
                currency: 'USD',
                costCenter: '',
                budgetAllocation: 0
              },
              performance: allocation.performance || {
                qualityScore: 0,
                timelinessScore: 0,
                safetyScore: 0,
                efficiencyScore: 0,
                overallScore: 0
              },
              location: allocation.location || {
                primaryLocation: '',
                secondaryLocations: [],
                travelRequired: false,
                travelDistance: 0,
                accommodationRequired: false,
                accommodationType: ''
              },
              communication: allocation.communication || {
                reportingFrequency: 'weekly',
                reportingMethod: 'email',
                supervisor: '',
                teamLead: '',
                communicationChannels: []
              },
              quality: allocation.quality || {
                qualityStandards: [],
                safetyRequirements: [],
                complianceStandards: [],
                inspectionSchedule: [],
                qualityMetrics: []
              },
              risk: allocation.risk || {
                identifiedRisks: [],
                riskLevel: 'medium',
                mitigationStrategies: [],
                contingencyPlans: [],
                riskOwner: ''
              },
              approval: {
                ...allocation.approval,
                approvalDate: allocation.approval?.approvalDate ? new Date(allocation.approval.approvalDate).toISOString().split('T')[0] : '',
                nextReviewDate: allocation.approval?.nextReviewDate ? new Date(allocation.approval.nextReviewDate).toISOString().split('T')[0] : ''
              } || {
                approver: '',
                approvalDate: '',
                status: 'pending',
                notes: '',
                nextReviewDate: ''
              },
              notifications: allocation.notifications || {
                emailAlerts: true,
                smsAlerts: false,
                portalNotifications: true,
                escalationEnabled: true,
                reminderFrequency: 'daily'
              },
              settings: allocation.settings || {
                autoAllocation: false,
                requireApproval: true,
                publicVisibility: false,
                allowModifications: true,
                trackTime: true,
                trackCosts: true
              },
              recurring: {
                ...allocation.recurring,
                startDate: allocation.recurring?.startDate ? new Date(allocation.recurring.startDate).toISOString().split('T')[0] : '',
                endDate: allocation.recurring?.endDate ? new Date(allocation.recurring.endDate).toISOString().split('T')[0] : '',
                nextAllocationDate: allocation.recurring?.nextAllocationDate ? new Date(allocation.recurring.nextAllocationDate).toISOString().split('T')[0] : ''
              } || {
                enabled: false,
                frequency: 'weekly',
                startDate: '',
                endDate: '',
                nextAllocationDate: '',
                cycles: 0
              },
              dependencies: allocation.dependencies || {
                prerequisiteAllocations: [],
                dependentAllocations: [],
                criticalPath: false,
                slackTime: 0
              },
              constraints: allocation.constraints || {
                timeConstraints: [],
                resourceConstraints: [],
                budgetConstraints: [],
                skillConstraints: [],
                locationConstraints: []
              }
            });
          } else {
            showError('Failed to load resource allocation details.');
            navigate('/subcontracting/resources');
          }
        } catch (err) {
          console.error('Error loading resource allocation:', err);
          showError('Failed to load resource allocation details.');
          navigate('/subcontracting/resources');
        } finally {
          setLoading(false);
        }
    };
    loadAllocationData();
  }, [finalAllocationId]);

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Allocation Details',
      icon: <Target size={18} />
    },
    {
      id: 'resources',
      label: 'Resources & Skills',
      icon: <Users size={18} />
    },
    {
      id: 'scheduling',
      label: 'Scheduling & Costs',
      icon: <Calendar size={18} />
    },
    {
      id: 'management',
      label: 'Management & Review',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };
  
  const addNestedArrayItem = (parent, key, value) => {
  if (!value) return;
  // If value is string → wrap into object
  const normalizedValue =
    typeof value === 'string'
      ? { name: value.trim() }
      : value;
      
      if (!normalizedValue.name) return;
      
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [key]: [...(prev[parent][key] || []), normalizedValue]
        }
      }));
  };
  
  const removeNestedArrayItem = (parent, key, index) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: prev[parent][key].filter((_, i) => i !== index)
      }
    }));
  };

  const handleResourceAdd = (resourceType, resource) => {
    setFormData(prev => ({
      ...prev,
      resources: {
        ...prev.resources,
        [resourceType]: [...(prev.resources[resourceType] || []), resource]
      }
    }));
  };

  const handleResourceRemove = (resourceType, index) => {
    setFormData(prev => ({
      ...prev,
      resources: {
        ...prev.resources,
        [resourceType]: (prev.resources[resourceType] || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleScoreChange = (field, value) => {
    if (finalViewMode) return;
    const score = Math.max(0, Math.min(100, parseInt(value) || 0));
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        performance: {
          ...prev.performance,
          [field]: score
        }
      };
      
      // Auto-calculate overall score
      const scores = [
        updatedData.performance.qualityScore,
        updatedData.performance.timelinessScore,
        updatedData.performance.safetyScore,
        updatedData.performance.efficiencyScore
      ];
      
      const overall = Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
      updatedData.performance.overallScore = overall;
      
      return updatedData;
    });
  };

  // Get input props for view mode
  const getInputProps = () => ({
    disabled: finalViewMode,
    readOnly: finalViewMode
  });

  // Handle subcontractor selection to auto-populate fields
  const handleSubcontractorSelect = (e) => {
    const selectedId = e.target.value;
    const selectedSubcontractor = subcontractors.find( sub => (sub._id === selectedId) || (sub.id === selectedId));

    if (selectedSubcontractor) {
      setFormData(prev => ({
        ...prev,
        subcontractor: {
          id: selectedId,
          name: selectedSubcontractor.name || '',
          company: selectedSubcontractor.name || '',
          contactPerson: selectedSubcontractor.primaryContact?.name || '',
          email: selectedSubcontractor.primaryContact?.email || '',
          phone: selectedSubcontractor.primaryContact?.phone || '',
          address: selectedSubcontractor.addresses?.headquarters?.street || ''
        },
        // Auto-populate costs from subcontractor's business details
        costs: {
          ...prev.costs,
          hourlyRate: selectedSubcontractor.businessDetails?.hourlyRate || prev.costs.hourlyRate,
          dailyRate: selectedSubcontractor.businessDetails?.dailyRate || prev.costs.dailyRate,
          currency: selectedSubcontractor.businessDetails?.currency || prev.costs.currency
        },
        // Auto-populate location from subcontractor
        location: {
          ...prev.location,
          primaryLocation: selectedSubcontractor.addresses?.headquarters 
            ? `${selectedSubcontractor.addresses.headquarters.city || ''}, ${selectedSubcontractor.addresses.headquarters.state || ''}`.trim()
            : prev.location.primaryLocation
        },
        // Auto-populate requirements from subcontractor's service categories
        requirements: {
          ...prev.requirements,
          requiredSkills: selectedSubcontractor.serviceCategories?.map(cat => cat.category).filter(Boolean) || prev.requirements.requiredSkills,
          certifications: selectedSubcontractor.compliance?.certifications?.map(cert => 
            typeof cert === 'string' ? cert : cert.name
          ).filter(Boolean) || prev.requirements.certifications
        }
      }));
    }
  };

  // Handle project selection to auto-populate fields
  const handleProjectSelect = (e) => {
    const selectedId = e.target.value;
    const selectedProject = projects.find(proj => (proj._id || proj.id) === selectedId);

    if (selectedProject) {
      setFormData(prev => ({
        ...prev,
        project: {
          id: selectedId,
          name: selectedProject.name || '',
          description: selectedProject.description || '',
          startDate: selectedProject.startDate ? new Date(selectedProject.startDate).toISOString().split('T')[0] : '',
          endDate: selectedProject.endDate ? new Date(selectedProject.endDate).toISOString().split('T')[0] : '',
          budget: selectedProject.budget?.toString() || '',
          status: selectedProject.status || ''
        },
        // Auto-populate allocation dates from project if not already set
        startDate: prev.startDate || (selectedProject.startDate ? new Date(selectedProject.startDate).toISOString().split('T')[0] : ''),
        endDate: prev.endDate || (selectedProject.endDate ? new Date(selectedProject.endDate).toISOString().split('T')[0] : ''),
        // Auto-populate costs budget from project
        costs: {
          ...prev.costs,
          budgetAllocation: selectedProject.budget || prev.costs.budgetAllocation
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (finalViewMode) return;
    
    setIsSubmitting(true);
    console.log('[ResourceAllocation] handleSubmit called - Mode:', finalEditMode ? 'EDIT' : 'CREATE', 'AllocationID:', finalAllocationId);
    
    try {
      // Validate required fields
      if (!formData.allocationName || !formData.subcontractor.id || !formData.startDate || !formData.endDate) {
        const errorMsg = 'Please fill in all required fields (Allocation Name, Subcontractor, Start Date, and End Date).';
        console.warn('[ResourceAllocation] Validation failed:', {
          allocationName: formData.allocationName,
          subcontractorId: formData.subcontractor.id,
          startDate: formData.startDate,
          endDate: formData.endDate
        });
        showError(errorMsg);
        setIsSubmitting(false);
        return;
      }

      console.log('[ResourceAllocation] Validation passed. Preparing request data...');

      // Format data for backend - convert date strings to ISO strings and properly structure nested objects
      const allocationData = {
        allocationName: formData.allocationName,
        allocationType: formData.allocationType,
        status: formData.status,
        priority: formData.priority,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        duration: formData.duration || 0,
        subcontractor: {
          id: formData.subcontractor.id,
          name: formData.subcontractor.name,
          company: formData.subcontractor.company,
          contactPerson: formData.subcontractor.contactPerson,
          email: formData.subcontractor.email,
          phone: formData.subcontractor.phone,
          address: formData.subcontractor.address
        },
        project: formData.project.id ? {
          id: formData.project.id,
          name: formData.project.name,
          description: formData.project.description,
          startDate: formData.project.startDate ? new Date(formData.project.startDate).toISOString() : undefined,
          endDate: formData.project.endDate ? new Date(formData.project.endDate).toISOString() : undefined,
          budget: formData.project.budget ? parseFloat(formData.project.budget) : undefined,
          status: formData.project.status
        } : undefined,
        resources: {
          humanResources: formData.resources.humanResources || [],
          equipment: formData.resources.equipment || [],
          materials: formData.resources.materials || [],
          tools: formData.resources.tools || [],
          vehicles: formData.resources.vehicles || [],
          facilities: formData.resources.facilities || []
        },
        allocation: {
          totalCapacity: formData.allocation.totalCapacity || 0,
          allocatedCapacity: formData.allocation.allocatedCapacity || 0,
          availableCapacity: formData.allocation.availableCapacity || 0,
          utilizationRate: formData.allocation.utilizationRate || 0,
          efficiency: formData.allocation.efficiency || 0,
          productivity: formData.allocation.productivity || 0
        },
        schedule: {
          workDays: formData.schedule.workDays || [],
          workHours: formData.schedule.workHours || 8,
          shiftPattern: formData.schedule.shiftPattern || 'day',
          overtimeAllowed: formData.schedule.overtimeAllowed || false,
          weekendWork: formData.schedule.weekendWork || false,
          holidayWork: formData.schedule.holidayWork || false
        },
        requirements: {
          requiredSkills: formData.requirements.requiredSkills.filter(Boolean),
          certifications: formData.requirements.certifications.filter(Boolean),
          experience: formData.requirements.experience || '',
          education: formData.requirements.education || '',
          language: formData.requirements.language || '',
          physicalRequirements: formData.requirements.physicalRequirements.filter(Boolean)
        },
        costs: {
          hourlyRate: formData.costs.hourlyRate || 0,
          dailyRate: formData.costs.dailyRate || 0,
          weeklyRate: formData.costs.weeklyRate || 0,
          monthlyRate: formData.costs.monthlyRate || 0,
          totalCost: formData.costs.totalCost || 0,
          currency: formData.costs.currency || 'USD',
          costCenter: formData.costs.costCenter || '',
          budgetAllocation: formData.costs.budgetAllocation || 0
        },
        performance: {
          qualityScore: formData.performance.qualityScore || 0,
          timelinessScore: formData.performance.timelinessScore || 0,
          safetyScore: formData.performance.safetyScore || 0,
          efficiencyScore: formData.performance.efficiencyScore || 0,
          overallScore: formData.performance.overallScore || 0
        },
        location: {
          primaryLocation: formData.location.primaryLocation || '',
          secondaryLocations: formData.location.secondaryLocations.filter(Boolean),
          travelRequired: formData.location.travelRequired || false,
          travelDistance: formData.location.travelDistance || 0,
          accommodationRequired: formData.location.accommodationRequired || false,
          accommodationType: formData.location.accommodationType || ''
        },
        communication: {
          reportingFrequency: formData.communication.reportingFrequency || 'weekly',
          reportingMethod: formData.communication.reportingMethod || 'email',
          supervisor: formData.communication.supervisor || '',
          teamLead: formData.communication.teamLead || '',
          communicationChannels: formData.communication.communicationChannels.filter(Boolean)
        },
        quality: {
          qualityStandards: formData.quality.qualityStandards.filter(Boolean),
          safetyRequirements: formData.quality.safetyRequirements.filter(Boolean),
          complianceStandards: formData.quality.complianceStandards.filter(Boolean),
          inspectionSchedule: formData.quality.inspectionSchedule.filter(Boolean),
          qualityMetrics: formData.quality.qualityMetrics.filter(Boolean)
        },
        risk: {
          identifiedRisks: formData.risk.identifiedRisks.filter(Boolean),
          riskLevel: formData.risk.riskLevel || 'medium',
          mitigationStrategies: formData.risk.mitigationStrategies.filter(Boolean),
          contingencyPlans: formData.risk.contingencyPlans.filter(Boolean),
          riskOwner: formData.risk.riskOwner || ''
        },
        approval: {
          approver: formData.approval.approver || '',
          approvalDate: formData.approval.approvalDate ? new Date(formData.approval.approvalDate).toISOString() : undefined,
          status: formData.approval.status || 'pending',
          notes: formData.approval.notes || '',
          nextReviewDate: formData.approval.nextReviewDate ? new Date(formData.approval.nextReviewDate).toISOString() : undefined
        },
        notifications: {
          emailAlerts: formData.notifications.emailAlerts !== undefined ? formData.notifications.emailAlerts : true,
          smsAlerts: formData.notifications.smsAlerts || false,
          portalNotifications: formData.notifications.portalNotifications !== undefined ? formData.notifications.portalNotifications : true,
          escalationEnabled: formData.notifications.escalationEnabled !== undefined ? formData.notifications.escalationEnabled : true,
          reminderFrequency: formData.notifications.reminderFrequency || 'daily'
        },
        settings: {
          autoAllocation: formData.settings.autoAllocation || false,
          requireApproval: formData.settings.requireApproval !== undefined ? formData.settings.requireApproval : true,
          publicVisibility: formData.settings.publicVisibility || false,
          allowModifications: formData.settings.allowModifications !== undefined ? formData.settings.allowModifications : true,
          trackTime: formData.settings.trackTime !== undefined ? formData.settings.trackTime : true,
          trackCosts: formData.settings.trackCosts !== undefined ? formData.settings.trackCosts : true
        },
        recurring: {
          enabled: formData.recurring.enabled || false,
          frequency: formData.recurring.frequency || 'weekly',
          startDate: formData.recurring.startDate ? new Date(formData.recurring.startDate).toISOString() : undefined,
          endDate: formData.recurring.endDate ? new Date(formData.recurring.endDate).toISOString() : undefined,
          nextAllocationDate: formData.recurring.nextAllocationDate ? new Date(formData.recurring.nextAllocationDate).toISOString() : undefined,
          cycles: formData.recurring.cycles || 0
        },
        dependencies: {
          prerequisiteAllocations: formData.dependencies.prerequisiteAllocations || [],
          dependentAllocations: formData.dependencies.dependentAllocations || [],
          criticalPath: formData.dependencies.criticalPath || false,
          slackTime: formData.dependencies.slackTime || 0
        },
        constraints: {
          timeConstraints: formData.constraints.timeConstraints.filter(Boolean),
          resourceConstraints: formData.constraints.resourceConstraints.filter(Boolean),
          budgetConstraints: formData.constraints.budgetConstraints.filter(Boolean),
          skillConstraints: formData.constraints.skillConstraints.filter(Boolean),
          locationConstraints: formData.constraints.locationConstraints.filter(Boolean)
        }
      };

      // Remove undefined values
      const cleanData = JSON.parse(JSON.stringify(allocationData));

      console.log('[ResourceAllocation] Sending request to backend...');
      console.log('[ResourceAllocation] Request data:', cleanData);

      let response;
      if (finalEditMode && finalAllocationId) {
        console.log(`[ResourceAllocation] UPDATE mode - Calling updateResourceAllocation(${finalAllocationId})`);
        try {
          response = await ResourceAllocationService.updateResourceAllocation(finalAllocationId, cleanData);
          console.log('[ResourceAllocation] Update response:', response);
          
          if (response && response.success) {
            console.log('[ResourceAllocation] Update successful! Showing success message...');
            success('Resource allocation updated successfully!');
            // Wait for user to see the success message before navigating
            setTimeout(() => {
              console.log('[ResourceAllocation] Navigating to resources list...');
              navigate('/subcontracting/resources');
            }, 1500);
          } else {
            const errorMsg = response?.message || response?.error || 'Failed to update resource allocation. Please try again.';
            console.error('[ResourceAllocation] Update failed:', errorMsg, 'Full response:', response);
            showError(errorMsg);
          }
        } catch (serviceError) {
          console.error('[ResourceAllocation] Service error during update:', serviceError);
          const errorMsg = serviceError?.message || 'Failed to update resource allocation. Please check your connection and try again.';
          showError(errorMsg);
        }
      } else {
        console.log('[ResourceAllocation] CREATE mode - Calling createResourceAllocation');
        try {
          response = await ResourceAllocationService.createResourceAllocation(cleanData);
          console.log('[ResourceAllocation] Create response:', response);
          
          if (response && response.success) {
            console.log('[ResourceAllocation] Create successful! Showing success message...');
            success('Resource allocation created successfully!');
            // Wait for user to see the success message before navigating
            setTimeout(() => {
              console.log('[ResourceAllocation] Navigating to resources list...');
              navigate('/subcontracting/resources');
            }, 1500);
          } else {
            const errorMsg = response?.message || response?.error || 'Failed to create resource allocation. Please try again.';
            console.error('[ResourceAllocation] Create failed:', errorMsg, 'Full response:', response);
            showError(errorMsg);
          }
        } catch (serviceError) {
          console.error('[ResourceAllocation] Service error during create:', serviceError);
          const errorMsg = serviceError?.message || 'Failed to create resource allocation. Please check your connection and try again.';
          showError(errorMsg);
        }
      }
    } catch (err) {
      console.error(`[ResourceAllocation] Unexpected error during ${finalEditMode ? 'update' : 'create'}:`, err);
      const errorMessage = err?.message || err?.response?.data?.message || `Failed to ${finalEditMode ? 'update' : 'create'} resource allocation. Please try again.`;
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderAllocationDetails();
      case 'resources':
        return renderResourcesSkills();
      case 'scheduling':
        return renderSchedulingCosts();
      case 'management':
        return renderManagementReview();
      default:
        return renderAllocationDetails();
    }
  };

  const renderAllocationDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Resource Allocation Details</h3>
        <p className="section-description">Basic allocation information and project details</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Allocation ID</label>
          <input
            type="text"
            className="form-control"
            value={formData.allocationId}
            onChange={(e) => handleInputChange('allocationId', e.target.value)}
            placeholder="Auto-generated if left empty"
            {...getInputProps()}
          />
          <small className="form-text text-muted">Leave empty to auto-generate</small>
        </div>
        
        <div className="form-group">
          <label className="form-label">Allocation Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.allocationName}
            onChange={(e) => handleInputChange('allocationName', e.target.value)}
            placeholder="Enter allocation name"
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Allocation Type</label>
          <select
            className="form-select"
            value={formData.allocationType}
            onChange={(e) => handleInputChange('allocationType', e.target.value)}
            {...getInputProps()}
          >
            <option value="project">Project</option>
            <option value="task">Task</option>
            <option value="maintenance">Maintenance</option>
            <option value="support">Support</option>
            <option value="training">Training</option>
            <option value="consulting">Consulting</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            {...getInputProps()}
          >
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            {...getInputProps()}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Start Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">End Date *</label>
          <input
            type="date"
            className="form-control"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Duration (Days)</label>
          <input
            type="number"
            className="form-control"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            min="0"
            {...getInputProps()}
          />
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Subcontractor Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Subcontractor *</label>
            <select
              className="form-select"
              value={formData.subcontractor.id}
              onChange={handleSubcontractorSelect}
              required
              {...getInputProps()}
            >
              <option value="">Select subcontractor</option>
              {subcontractors.map((sub) => (
                <option key={sub._id} value={sub._id}>
                  {sub.name || sub.subcontractorId}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Contact Person</label>
            <input
              type="text"
              className="form-control"
              value={formData.subcontractor.contactPerson}
              onChange={(e) => handleNestedInputChange('subcontractor', 'contactPerson', e.target.value)}
              placeholder="Enter contact person name"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.subcontractor.email}
              onChange={(e) => handleNestedInputChange('subcontractor', 'email', e.target.value)}
              placeholder="Enter email address"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.subcontractor.phone}
              onChange={(e) => handleNestedInputChange('subcontractor', 'phone', e.target.value)}
              placeholder="Enter phone number"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Project Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={formData.project.id}
              onChange={handleProjectSelect}
              {...getInputProps()}
            >
              <option value="">Select project (optional)</option>
              {projects.map((proj) => (
                <option key={proj._id || proj.id} value={proj._id || proj.id}>
                  {proj.name || proj.projectCode}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.project.name}
              onChange={(e) => handleNestedInputChange('project', 'name', e.target.value)}
              placeholder="Project name (auto-filled if project selected)"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Budget</label>
            <input
              type="number"
              className="form-control"
              value={formData.project.budget}
              onChange={(e) => handleNestedInputChange('project', 'budget', e.target.value)}
              placeholder="Enter project budget"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Project Status</label>
            <input
              type="text"
              className="form-control"
              value={formData.project.status}
              onChange={(e) => handleNestedInputChange('project', 'status', e.target.value)}
              placeholder="Project status (auto-filled if project selected)"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderResourcesSkills = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Resources & Skills</h3>
        <p className="section-description">Resource allocation and skill requirements</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Human Resources</h4>
        
        <div className="array-input-group">
          {formData.resources.humanResources.map((resource, index) => (
            <div key={index} className="array-input-item">
              <input
              type="text"
              className="form-control"
              value={resource.name}
              onChange={(e) => {
                const updated = [...formData.resources.humanResources];
                updated[index] = { ...updated[index], name: e.target.value };
                
                setFormData(prev => ({
                  ...prev,
                  resources: {
                    ...prev.resources,
                    humanResources: updated
                  }
                }));
              }}
              {...getInputProps()}
            />
            
            {!finalViewMode && (
              <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() =>
                removeNestedArrayItem('resources', 'humanResources', index)
              }
              {...getInputProps()}
              >
              <X size={16} />
              </button>
            )}
          </div>
        ))}
        
        {!finalViewMode && (
          <div className="array-input-add">
            <input
            type="text"
            className="form-control"
            placeholder="Add human resource"
            value={humanResourceInput}
            onChange={(e) => setHumanResourceInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNestedArrayItem('resources', 'humanResources', {
                  name: humanResourceInput
                });
                setHumanResourceInput('');
              }
            }}
            {...getInputProps()}
            />
            
            <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              addNestedArrayItem('resources', 'humanResources', {
                name: humanResourceInput
              });
              setHumanResourceInput('');
            }}
            {...getInputProps()}
            >
            <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    </div>

    <div className="form-group">
      <h4 className="section-subtitle">Equipment</h4>
      
      <div className="array-input-group">
        {formData.resources.equipment.map((equipment, index) => (
          <div key={index} className="array-input-item">
            <input
            type="text"
            className="form-control"
            value={equipment.name}
            onChange={(e) => {
              const updated = [...formData.resources.equipment];
              updated[index] = { ...updated[index], name: e.target.value };
              
              setFormData(prev => ({
                ...prev,
                resources: {
                  ...prev.resources,
                  equipment: updated
                }
              }));
            }}
            {...getInputProps()}
            />
            
            {!finalViewMode && (
              <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() =>
                removeNestedArrayItem('resources', 'equipment', index)
              }
              {...getInputProps()}
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
        
        {!finalViewMode && (
          <div className="array-input-add">
          <input
          type="text"
          className="form-control"
          placeholder="Add equipment"
          value={equipmentInput}
          onChange={(e) => setEquipmentInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addNestedArrayItem('resources', 'equipment', {
                name: equipmentInput
              });
              setEquipmentInput('');
            }
          }}
          {...getInputProps()}
          />
          
          <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => {
            addNestedArrayItem('resources', 'equipment', {
              name: equipmentInput
            });
            setEquipmentInput('');
          }}
          {...getInputProps()}
          >
            <Plus size={16} />
          </button>
          </div>
        )}
      </div>
    </div>
    
    <div className="form-group">
    <h4 className="section-subtitle">Required Skills</h4>
    
    <div className="array-input-group">
      {formData.requirements.requiredSkills.map((skill, index) => (
        <div key={index} className="array-input-item">
        <input
          type="text"
          className="form-control"
          value={skill}
          onChange={(e) => {
            const updated = [...formData.requirements.requiredSkills];
            updated[index] = e.target.value;

            setFormData(prev => ({
              ...prev,
              requirements: {
                ...prev.requirements,
                requiredSkills: updated
              }
            }));
          }}
          placeholder="Enter required skill"
          {...getInputProps()}
        />

        {!finalViewMode && (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() =>
              removeNestedArrayItem('requirements', 'requiredSkills', index)
            }
          >
            <X size={16} />
          </button>
        )}
      </div>
    ))}

    {!finalViewMode && (
      <div className="array-input-add">
        <input
        type="text"
        className="form-control"
        placeholder="Add required skill"
        value={requiredSkillInput}
        onChange={(e) => setRequiredSkillInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (!requiredSkillInput.trim()) return;
            
            setFormData(prev => ({
              ...prev,
              requirements: {
                ...prev.requirements,
                requiredSkills: [
                  ...prev.requirements.requiredSkills,
                  requiredSkillInput.trim()
                ]
              }
            }));
            setRequiredSkillInput('');
          }
        }}
        {...getInputProps()}
       />
       
       <button
       type="button"
       className="btn btn-outline-primary"
       onClick={() => {
        if (!requiredSkillInput.trim()) return;
        setFormData(prev => ({
          ...prev,
          requirements: {
            ...prev.requirements,
            requiredSkills: [
              ...prev.requirements.requiredSkills,
              requiredSkillInput.trim()
            ]
          }
        }));
        setRequiredSkillInput('');
       }}
       >
          <Plus size={16} />
       </button>
      </div>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Allocation Metrics</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Total Capacity</label>
            <input
              type="number"
              className="form-control"
              value={formData.allocation.totalCapacity}
              onChange={(e) => handleNestedInputChange('allocation', 'totalCapacity', e.target.value)}
              min="0"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Allocated Capacity</label>
            <input
              type="number"
              className="form-control"
              value={formData.allocation.allocatedCapacity}
              onChange={(e) => handleNestedInputChange('allocation', 'allocatedCapacity', e.target.value)}
              min="0"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Available Capacity</label>
            <input
              type="number"
              className="form-control"
              value={formData.allocation.availableCapacity}
              onChange={(e) => handleNestedInputChange('allocation', 'availableCapacity', e.target.value)}
              min="0"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Utilization Rate (%)</label>
            <input
              type="number"
              className="form-control"
              value={formData.allocation.utilizationRate}
              onChange={(e) => handleNestedInputChange('allocation', 'utilizationRate', e.target.value)}
              min="0"
              max="100"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchedulingCosts = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Scheduling & Costs</h3>
        <p className="section-description">Schedule configuration and cost management</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Schedule Configuration</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Work Hours per Day</label>
            <input
              type="number"
              className="form-control"
              value={formData.schedule.workHours}
              onChange={(e) => handleNestedInputChange('schedule', 'workHours', e.target.value)}
              min="1"
              max="24"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Shift Pattern</label>
            <select
              className="form-select"
              value={formData.schedule.shiftPattern}
              onChange={(e) => handleNestedInputChange('schedule', 'shiftPattern', e.target.value)}
              {...getInputProps()}
            >
              <option value="day">Day Shift</option>
              <option value="night">Night Shift</option>
              <option value="rotating">Rotating</option>
              <option value="flexible">Flexible</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Overtime Allowed</label>
            <select
              className="form-select"
              value={formData.schedule.overtimeAllowed}
              onChange={(e) => handleNestedInputChange('schedule', 'overtimeAllowed', e.target.value)}
              {...getInputProps()}
            >
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Weekend Work</label>
            <select
              className="form-select"
              value={formData.schedule.weekendWork}
              onChange={(e) => handleNestedInputChange('schedule', 'weekendWork', e.target.value)}
              {...getInputProps()}
            >
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Cost Information</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Hourly Rate</label>
            <input
              type="number"
              className="form-control"
              value={formData.costs.hourlyRate}
              onChange={(e) => handleNestedInputChange('costs', 'hourlyRate', e.target.value)}
              min="0"
              step="0.01"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Daily Rate</label>
            <input
              type="number"
              className="form-control"
              value={formData.costs.dailyRate}
              onChange={(e) => handleNestedInputChange('costs', 'dailyRate', e.target.value)}
              min="0"
              step="0.01"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Weekly Rate</label>
            <input
              type="number"
              className="form-control"
              value={formData.costs.weeklyRate}
              onChange={(e) => handleNestedInputChange('costs', 'weeklyRate', e.target.value)}
              min="0"
              step="0.01"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Monthly Rate</label>
            <input
              type="number"
              className="form-control"
              value={formData.costs.monthlyRate}
              onChange={(e) => handleNestedInputChange('costs', 'monthlyRate', e.target.value)}
              min="0"
              step="0.01"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Total Cost</label>
            <input
              type="number"
              className="form-control"
              value={formData.costs.totalCost}
              onChange={(e) => handleNestedInputChange('costs', 'totalCost', e.target.value)}
              min="0"
              step="0.01"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select
              className="form-select"
              value={formData.costs.currency}
              onChange={(e) => handleNestedInputChange('costs', 'currency', e.target.value)}
              {...getInputProps()}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
              <option value="AUD">AUD</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Performance Metrics</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Quality Score: {formData.performance.qualityScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.performance.qualityScore}
              onChange={(e) => handleScoreChange('qualityScore', e.target.value)}
              {...getInputProps()}            
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Timeliness Score: {formData.performance.timelinessScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.performance.timelinessScore}
              onChange={(e) => handleScoreChange('timelinessScore', e.target.value)}
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Safety Score: {formData.performance.safetyScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.performance.safetyScore}
              onChange={(e) => handleScoreChange('safetyScore', e.target.value)}
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Efficiency Score: {formData.performance.efficiencyScore}%</label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={formData.performance.efficiencyScore}
              onChange={(e) => handleScoreChange('efficiencyScore', e.target.value)}
              {...getInputProps()}
            />
          </div>
        </div>
        
        <div className="form-group">
          <div className="score-display">
            <h4>Overall Score: {formData.performance.overallScore}%</h4>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ width: `${formData.performance.overallScore}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderManagementReview = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Management & Review</h3>
        <p className="section-description">Approval process, communication, and management settings</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Approval Process</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Approver</label>
            <select
              className="form-select"
              value={formData.approval.approver}
              onChange={(e) => handleNestedInputChange('approval', 'approver', e.target.value)}
              {...getInputProps()}
            >
              <option value="">Select approver</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.approval.approvalDate}
              onChange={(e) => handleNestedInputChange('approval', 'approvalDate', e.target.value)}
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Approval Status</label>
            <select
              className="form-select"
              value={formData.approval.status}
              onChange={(e) => handleNestedInputChange('approval', 'status', e.target.value)}
              {...getInputProps()}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="under-review">Under Review</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Next Review Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.approval.nextReviewDate}
              onChange={(e) => handleNestedInputChange('approval', 'nextReviewDate', e.target.value)}
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Approval Notes</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.approval.notes}
          onChange={(e) => handleNestedInputChange('approval', 'notes', e.target.value)}
          placeholder="Enter approval notes and comments"
          {...getInputProps()}
        />
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Communication Settings</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Reporting Frequency</label>
            <select
              className="form-select"
              value={formData.communication.reportingFrequency}
              onChange={(e) => handleNestedInputChange('communication', 'reportingFrequency', e.target.value)}
              {...getInputProps()}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="as-needed">As Needed</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Reporting Method</label>
            <select
              className="form-select"
              value={formData.communication.reportingMethod}
              onChange={(e) => handleNestedInputChange('communication', 'reportingMethod', e.target.value)}
              {...getInputProps()}
            >
              <option value="email">Email</option>
              <option value="portal">Portal</option>
              <option value="phone">Phone</option>
              <option value="meeting">Meeting</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Supervisor</label>
            <select
              className="form-select"
              value={formData.communication.supervisor}
              onChange={(e) => handleNestedInputChange('communication', 'supervisor', e.target.value)}
              {...getInputProps()}
            >
              <option value="">Select supervisor</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Team Lead</label>
            <select
              className="form-select"
              value={formData.communication.teamLead}
              onChange={(e) => handleNestedInputChange('communication', 'teamLead', e.target.value)}
              {...getInputProps()}
            >
              <option value="">Select team lead</option>
              <option value="john-doe">John Doe</option>
              <option value="jane-smith">Jane Smith</option>
              <option value="mike-johnson">Mike Johnson</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Notification Settings</h4>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.emailAlerts}
              onChange={(e) => handleNestedInputChange('notifications', 'emailAlerts', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Email Alerts</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.smsAlerts}
              onChange={(e) => handleNestedInputChange('notifications', 'smsAlerts', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">SMS Alerts</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.portalNotifications}
              onChange={(e) => handleNestedInputChange('notifications', 'portalNotifications', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Portal Notifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.notifications.escalationEnabled}
              onChange={(e) => handleNestedInputChange('notifications', 'escalationEnabled', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Escalation Enabled</label>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Advanced Settings</h4>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.autoAllocation}
              onChange={(e) => handleNestedInputChange('settings', 'autoAllocation', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Auto Allocation</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.requireApproval}
              onChange={(e) => handleNestedInputChange('settings', 'requireApproval', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Require Approval</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.allowModifications}
              onChange={(e) => handleNestedInputChange('settings', 'allowModifications', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Allow Modifications</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.trackTime}
              onChange={(e) => handleNestedInputChange('settings', 'trackTime', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Track Time</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.settings.trackCosts}
              onChange={(e) => handleNestedInputChange('settings', 'trackCosts', e.target.checked)}
              {...getInputProps()}
            />
            <label className="form-check-label">Track Costs</label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={
          finalViewMode ? 'View Resource Allocation' : finalEditMode ? 'Edit Resource Allocation': 'Create Resource Allocation'
        }
        description="Comprehensive performance evaluation and feedback system"
      />
      <div className={`page-container ${finalViewMode ? 'view-mode' : ''}`} style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/subcontracting/resources')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                {finalEditMode ? 'Updating Allocation...' : 'Creating Allocation...'}
                </>
              ) : (
              <>
              <Save size={18} />
              {finalEditMode ? 'Update Resource Allocation' : 'Create Resource Allocation'}
              </>
            )}
            </button>
          </div>
        </form>
        </div>
      </div>
      <style>{`
      .view-mode input,

      .view-mode select,

      .view-mode textarea {
        opacity: 0.7;
        background-color: #f8f9fa !important;
      }
        
      .view-mode .array-input-add,
      
      .view-mode .btn-outline-danger,
      
      .view-mode .btn-outline-primary {
        display: none !important;
      }
        
      .view-mode button[type="submit"] {
        display: none !important;
      }
    `}</style>
    <ToastContainer />
    </>
    
  );
};

export default CreateResourceAllocation;
